import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { ExtraPrices, Pricelist, Tariff } from '../../../../pricelists/pricelists.models';
import { env } from '../../../../../env/env';
import { OnlyNumbersDirective } from '../../../../_infrastructure/only-numbers-directive';

const POLISH_DATE_FORMATS = {
    parse: {
        dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' }
    },
    display: {
        dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
        monthYearLabel: { month: 'short', year: 'numeric' },
        dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
        monthYearA11yLabel: { month: 'long', year: 'numeric' }
    }
};

@Component({
    selector: 'pricelist-form-dialog',
    templateUrl: './pricelist-form-dialog.html',
    styleUrl: './pricelist-form-dialog.css',
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        OnlyNumbersDirective
    ],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: POLISH_DATE_FORMATS }
    ]
})
export class PricelistFormDialog implements OnInit {
    constructor(
        private httpClient: HttpClient,
        private dialogRef: MatDialogRef<PricelistFormDialog>,
        private dateAdapter: DateAdapter<Date>,
        private cd: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: { clientId: string, pricelistId?: string }) {
        this.isEditMode = !!data.pricelistId;
    }

    public readonly isEditMode: boolean;
    public validFrom: Date | null = null;
    public validTo: Date | null = null;
    public tariffs: Tariff[] = [];
    public extraPrices: ExtraPrices = { orders: 0, products: 0, auctions: 0, shipments: 0 };

    public ngOnInit(): void {
        this.dateAdapter.setLocale('pl-PL');

        if (this.isEditMode) {
            this.loadPricelist();
        } else {
            this.loadDefaultPricelist();
        }
    }

    private loadPricelist(): void {
        this.httpClient.get<Pricelist>(`${env.apiUrl}/clients/${this.data.clientId}/pricelists/${this.data.pricelistId}`).subscribe(pricelist => {
            this.validFrom = this.toDate(pricelist.validFrom);
            this.validTo = this.toDate(pricelist.validTo);
            this.tariffs = pricelist.tariffs;
            this.extraPrices = pricelist.extraPrices;
            this.cd.detectChanges();
        });
    }

    private loadDefaultPricelist(): void {
        this.httpClient.get<Pricelist>(`${env.apiUrl}/pricelists/default`).subscribe(pricelist => {
            this.tariffs = pricelist.tariffs;
            this.extraPrices = pricelist.extraPrices;
            this.renumberTiers();
            this.cd.detectChanges();
        });
    }

    public closeDialog(): void {
        this.dialogRef.close(false);
    }

    public addTariff(): void {
        this.tariffs.push({
            name: '',
            tier: 0,
            price: 0,
            orders: { from: 0, to: 0 },
            products: { from: 0, to: 0 },
            auctions: { from: 0, to: 0 },
            shipments: { from: 0, to: 0 }
        });
        this.renumberTiers();
    }

    public removeTariff(tariff: Tariff): void {
        this.tariffs = this.tariffs.filter(t => t !== tariff);
        this.renumberTiers();
    }

    public isValid(): boolean {
        if (!this.validFrom) return false;
        if (this.tariffs.length === 0) return false;

        return this.tariffs.every(t => {
            const price = Number(t.price);

            return !!t.name &&
                !Number.isNaN(price) && price >= 0 &&
                this.isValidRange(t.orders) &&
                this.isValidRange(t.products) &&
                this.isValidRange(t.auctions) &&
                this.isValidRange(t.shipments);
        });
    }

    private isValidRange(range: { from: number, to: number }): boolean {
        const from = Number(range.from);
        const to = Number(range.to);
        return !Number.isNaN(from) && !Number.isNaN(to) && from <= to;
    }

    private renumberTiers(): void {
        this.tariffs.forEach((t, index) => t.tier = index + 1);
    }

    public save(): void {
        if (!this.isValid()) return;

        const payload = {
            validFrom: this.formatDate(this.validFrom),
            validTo: this.formatDate(this.validTo),
            tariffs: this.tariffs.map(t => ({
                name: t.name,
                tier: t.tier,
                price: Number(t.price),
                orders: { from: Number(t.orders.from), to: Number(t.orders.to) },
                products: { from: Number(t.products.from), to: Number(t.products.to) },
                auctions: { from: Number(t.auctions.from), to: Number(t.auctions.to) },
                shipments: { from: Number(t.shipments.from), to: Number(t.shipments.to) }
            })),
            extraPrices: this.extraPrices
        };

        const request$ = this.isEditMode
            ? this.httpClient.put(`${env.apiUrl}/clients/${this.data.clientId}/pricelists/${this.data.pricelistId}`, payload)
            : this.httpClient.post(`${env.apiUrl}/clients/${this.data.clientId}/pricelists`, payload);

        request$.subscribe({
            next: () => this.dialogRef.close(true),
            error: (err) => {
                alert(err?.error ?? 'Błąd podczas zapisu');
            }
        });
    }

    private formatDate(date: Date | null): string | null {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private toDate(value: string | null): Date | null {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }
}
